import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'

import InfoBlock from '../components/InfoBlock'
import UnderwritersAndGuarantorsTable from '../components/UnderwritersAndGuarantorsTable'
import { calculateIssueTotalOfferingSize, formatFeeLevel } from './utils'

import { getLevelName } from './utils'
import { EnrichedIssueEntity, IssueEntity } from '../../types/IssueEntity'
import { isUnit } from '../../utils/issues/sharesOrUnits'
import { CommitChart } from '../../components/entities/CommitChart'

const Wrapper = ({ children, title }: { children: React.ReactNode; title: string }) => (
    <InfoBlock title={title} loadingElement={false}>
        {children}
    </InfoBlock>
)

function UnderwritersAndGuarantors({ issue }: { issue: EnrichedIssueEntity }) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const investors = issue.investors.filter((investor) => !investor.isSeller)

    const fees = (
        issue.fees.filter((fee) => fee.feeLevel !== null && fee.feeLevel > 0) as Array<
            IssueEntity['fees'][number] & { feeLevel: number }
        >
    ).sort((a, b) => a.feeLevel - b.feeLevel)
    const maxFeeLevel = Math.max(...issue.fees.map((level) => level.feeLevel))

    const hasNoCommitment = investors.length === 0

    const NoInvestorsAdditionalInformation = () => {
        return (
            <Stack spacing={2}>
                <Typography>{t('pages.issues.underwriters.commitmentInfoNotCommunicated')}</Typography>
                {fees.length > 0 ? (
                    <Box>
                        <Typography
                            sx={{
                                paddingBottom: 1,
                                fontWeight: 800,
                            }}
                        >
                            {t('pages.issues.underwriters.guarantorCompensations')}
                        </Typography>
                        <Table size='small'>
                            <TableBody>
                                {fees.map((fee) => {
                                    const feeLevelName = getLevelName(fee.feeLevel, maxFeeLevel)
                                    const formatedFeeLevel = formatFeeLevel(
                                        { fees: issue.fees, isUnit: isUnit(issue) },
                                        fee.feeLevel - 1,
                                        maxFeeLevel,
                                        locale,
                                    )
                                    return (
                                        <TableRow key={fee.feeLevel}>
                                            <TableCell width={110} padding='none' sx={{ borderBottom: 'none' }}>
                                                {feeLevelName}
                                            </TableCell>
                                            <TableCell sx={{ padding: '2px 12px', borderBottom: 'none' }}>
                                                {formatedFeeLevel}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                ) : null}
            </Stack>
        )
    }

    if (hasNoCommitment) {
        return (
            <Wrapper title={t('pages.issues.underwriters.title')}>
                <span>{t('pages.issues.underwriters.noCommitment')}</span>
            </Wrapper>
        )
    }

    const hasInvestors = investors.length > 0

    const isIpoOrRights = issue.type === 'Rights issue' || issue.type === 'IPO'
    const hasAnyTaWithoutRights = issue.investors.some(
        (inv) => !inv.isSeller && inv.feeLevel === 0 && (inv.withoutRights === true || inv.withoutRights === 1),
    )

    return (
        <Wrapper title={t('pages.issues.underwriters.title')}>
            <CommitChart issue={issue} isLeftAlign size='large' skipAnimation />
            {!hasInvestors && !issue.noInvestorsNamed ? <NoInvestorsAdditionalInformation /> : null}
            {hasInvestors && !issue.noInvestorsNamedChecked && (
                <UnderwritersAndGuarantorsTable
                    currency={issue.currency}
                    fees={issue.fees}
                    noGuarantors={!!issue.noGuarantors}
                    investors={issue.investors}
                    isUnit={isUnit(issue)}
                    totalOfferingSize={calculateIssueTotalOfferingSize(issue).totalOfferingSize}
                    splitFeeLevelZeroColumn={isIpoOrRights && hasAnyTaWithoutRights}
                />
            )}
        </Wrapper>
    )
}

export default UnderwritersAndGuarantors
